import { Injectable, inject } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { IChecklistTimeInterval, IChecklistTimeIntervalData } from '@fi-shared/checklist/checklist-v2';
import {
    FiChecklist2ScheduleIntervalService,
    IAuditOnDemandOption,
} from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { FiChecklistScheduleType } from '@fi-shared/checklist/schedule/schedule-edit/fi-checklist-schedule-type';
import { FiChecklistTimeIntervalDataType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-data-type';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { FiDateFormatOptions, isDefined } from '@fi-shared/utils';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';
import { FiLocaleService } from '@fi-shared/fi-locale.service';

@Injectable()
export class FiChecklist2ScheduleFormatService {
    private appTextService = inject(AppTextService);
    private fiLocaleService = inject(FiLocaleService);
    private fiFormatDateService = inject(FiFormatDateService);
    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);

    getScheduleTypeText(scheduleType: FiChecklistScheduleType, isListLabels?: boolean): string {
        switch (scheduleType) {
            case FiChecklistScheduleType.Recurring:
                return this.appTextService.get('checklist.recurring_tasks');
            case FiChecklistScheduleType.OneTime:
                return this.appTextService.get('checklist.one_time_checklist');
            case FiChecklistScheduleType.OnDemand:
                return this.appTextService.get(
                    isListLabels ? 'checklist.schedule_interval_on_demand' : 'checklist.on_demand_checklist',
                );
            case FiChecklistScheduleType.Audit:
                return this.appTextService.get(
                    isListLabels ? 'checklist.schedule_interval_audit' : 'checklist.audit_control',
                );
            case FiChecklistScheduleType.Unknown:
                return '';
        }

        throw new Error(`unknown schedule type ${scheduleType} in getScheduleTypeText`);
    }

    getScheduleTypeDescriptionText(scheduleType: FiChecklistScheduleType): string {
        switch (scheduleType) {
            case FiChecklistScheduleType.Recurring:
                return this.appTextService.get('checklist.recurring_tasks_descr');
            case FiChecklistScheduleType.OneTime:
                return this.appTextService.get('checklist.one_time_checklist_descr');
            case FiChecklistScheduleType.OnDemand:
                return this.appTextService.get('checklist.on_demand_checklist_descr');
            case FiChecklistScheduleType.Audit:
                return this.appTextService.get('checklist.audit_control_descr');
        }
        throw new Error(`unknown schedule type ${scheduleType} in getScheduleTypeDescriptionText`);
    }

    getTimeIntervalTypeText(timeIntervalType: FiChecklistTimeIntervalType): string {
        if (this.fiChecklist2ScheduleIntervalService.isDailyInterval(timeIntervalType)) {
            return this.appTextService.get('checklist.repeat_frequency_daily');
        }
        if (this.fiChecklist2ScheduleIntervalService.isWeeklyInterval(timeIntervalType)) {
            return this.appTextService.get('weekly');
        }
        if (this.fiChecklist2ScheduleIntervalService.isMonthlyInterval(timeIntervalType)) {
            return this.appTextService.get('checklist.repeat_frequency_monthly');
        }
        if (this.fiChecklist2ScheduleIntervalService.isYearlyInterval(timeIntervalType)) {
            return this.appTextService.get('checklist.repeat_frequency_yearly');
        }
        throw new Error(`unknown time interval type ${timeIntervalType} in getTimeIntervalTypeText`);
    }

    getTimeIntervalTypeDescriptionText(timeIntervalType: FiChecklistTimeIntervalType): string {
        if (this.fiChecklist2ScheduleIntervalService.isDailyInterval(timeIntervalType)) {
            return this.appTextService.get('checklist.repeat_frequency_daily_descr');
        }
        if (this.fiChecklist2ScheduleIntervalService.isWeeklyInterval(timeIntervalType)) {
            return this.appTextService.get('checklist.repeat_frequency_weekly_descr');
        }
        if (this.fiChecklist2ScheduleIntervalService.isMonthlyInterval(timeIntervalType)) {
            return this.appTextService.get('checklist.repeat_frequency_monthly_descr');
        }
        if (this.fiChecklist2ScheduleIntervalService.isYearlyInterval(timeIntervalType)) {
            return this.appTextService.get('checklist.repeat_frequency_yearly_descr');
        }
        throw new Error(`unknown time interval type ${timeIntervalType} in getTimeIntervalTypeDescriptionText`);
    }

    getHourMinuteInputData(intervalSettings: IChecklistTimeIntervalData, offsetMinute?: number): string {
        if (!isDefined(intervalSettings.Hour) || !isDefined(intervalSettings.Minute)) {
            return undefined;
        }

        if (!isDefined(offsetMinute)) {
            return `${this.convertTimeToTimepickerInput(intervalSettings.Hour)}:${this.convertTimeToTimepickerInput(intervalSettings.Minute)}`;
        }

        let hours: number = intervalSettings.Hour;
        let minutes: number = intervalSettings.Minute + offsetMinute;
        while (minutes < 0) {
            // fix for negative offsetMinutes
            // https://chainformation.atlassian.net/browse/FI-5799
            hours--;
            minutes += 60;
        }
        return (
            `${this.convertTimeToTimepickerInput(hours + Math.floor(minutes / 60))}` +
            ':' +
            `${this.convertTimeToTimepickerInput(minutes % 60)}`
        );
    }

    private readonly DEFAULT_TIME_LIMIT = '00:00';
    getTimeLimitByPrevDayTime(intervals: IChecklistTimeInterval[], endTimeMinutesOffset: number): string {
        if (!intervals) {
            return this.DEFAULT_TIME_LIMIT;
        }

        const latestEnd = intervals
            .filter((interval) => interval.IsEnabled)
            .map((interval) => interval.End)
            .reduce((latest, current) => {
                if (!latest) {
                    return current;
                }

                return this.getTimeIntervalDataAbsoluteMinutes(current) >
                    this.getTimeIntervalDataAbsoluteMinutes(latest)
                    ? current
                    : latest;
            }, undefined);

        if (!isDefined(latestEnd)) {
            return this.DEFAULT_TIME_LIMIT;
        }

        const timeLimit = this.getHourMinuteInputData(latestEnd, endTimeMinutesOffset);

        if (!timeLimit) {
            return this.DEFAULT_TIME_LIMIT;
        }

        const [rawHours, rawMinutes] = timeLimit.split(':');
        const hours = parseInt(rawHours, 10);

        const resultHours = this.resolveTimeLimitHours(hours, latestEnd.IsNextDay);

        const resultMinutes = this.resolveTimeLimitMinutes({
            minutes: rawMinutes,
            hours,
            intervalEndMinute: latestEnd.Minute ?? 0,
            isNextDay: latestEnd.IsNextDay,
        });

        return `${resultHours}:${resultMinutes}`;
    }

    private getTimeIntervalDataAbsoluteMinutes(end: IChecklistTimeIntervalData): number {
        const hours = (end.Hour ?? 0) + (end.IsNextDay ? 24 : 0);

        return hours * 60 + (end.Minute ?? 0);
    }

    private resolveTimeLimitHours(hours: number, isNextDay: boolean): string {
        if (hours >= 24) {
            return this.convertTimeToTimepickerInput(hours - 24);
        }

        if (isNextDay) {
            return this.convertTimeToTimepickerInput(hours);
        }

        return '00';
    }

    private resolveTimeLimitMinutes({
        minutes,
        hours,
        intervalEndMinute,
        isNextDay,
    }: {
        minutes: string;
        hours: number;
        intervalEndMinute: number;
        isNextDay: boolean;
    }): string {
        if (isNextDay || hours > 23 || (hours === 23 && intervalEndMinute >= 30)) {
            return minutes;
        }

        return '00';
    }

    getIntervalDataOnTimePickerChange(
        intervalData: IChecklistTimeIntervalData,
        timeValue: string,
    ): IChecklistTimeIntervalData {
        const [hour, minute] = timeValue?.split(':').map(Number) || [undefined, undefined];

        intervalData.Hour = hour;
        intervalData.Minute = minute;

        return intervalData;
    }

    convertTimeIntervalDataToDate(timeIntervalData: IChecklistTimeIntervalData): string {
        const date: Date = new Date(timeIntervalData.Year, timeIntervalData.Month - 1, timeIntervalData.Day);
        return this.fiFormatDateService.formatDate(date, FiDateFormatOptions, { showToday: false });
    }

    convertTimeToTimepickerInput(time: number): string {
        return `${time}`.length === 1 ? `0${time}` : `${time}`;
    }

    getPeriodName(intervalType: FiChecklistTimeIntervalType, periodIndex: number): string {
        switch (intervalType) {
            case FiChecklistTimeIntervalType.RecurringDaily:
                return this.fiLocaleService.getAllDayNames()[periodIndex];
            case FiChecklistTimeIntervalType.RecurringMonthly:
                return this.fiLocaleService.getMonthName(periodIndex);
            default:
                return `${this.appTextService.get('checklist.period')} ${periodIndex + 1}`;
        }
    }

    getPeriodNameAdvanced(
        intervalType: FiChecklistTimeIntervalType,
        interval: IChecklistTimeInterval,
        isStart?: boolean,
    ): string {
        const intervalData = isStart ? interval.Start : interval.End;
        if (!isDefined(intervalData)) {
            return;
        }

        if (this.fiChecklist2ScheduleIntervalService.isWeeklyInterval(intervalType)) {
            return this.fiLocaleService.getAllDayNames()[intervalData.DayOfWeek - 1];
        }

        const monthName: string = this.fiLocaleService.getMonthName(intervalData.Month - 1, true) + ', ';

        if (this.fiChecklist2ScheduleIntervalService.isMonthlyInterval(intervalType)) {
            return this.getMonthlyPeriodName(intervalData, monthName);
        }

        if (this.fiChecklist2ScheduleIntervalService.isYearlyInterval(intervalType)) {
            return monthName + intervalData.Day;
        }

        if (this.fiChecklist2ScheduleIntervalService.isOneTimeSchedule(intervalType)) {
            return this.convertTimeIntervalDataToDate(intervalData);
        }

        if (this.fiChecklist2ScheduleIntervalService.isAuditOrOnDemandInterval(intervalType)) {
            if (isStart) {
                return this.appTextService.get(
                    this.fiChecklist2ScheduleIntervalService.isAuditInterval(intervalType)
                        ? 'checklist.schedule_start_on_audit'
                        : 'checklist.schedule_start_on_demand',
                );
            }

            return this.getAuditOnDemandEndIntervalLabel(intervalData);
        }
    }

    getAuditIntervalStartDate(interval: IChecklistTimeInterval): string {
        if (!Object.keys(interval.Start).length) return;

        return `${this.convertTimeIntervalDataToDate(interval.Start)} ${this.getHourMinuteInputData(interval.Start)}`;
    }

    getAuditOnDemandEndIntervalLabel(intervalData: IChecklistTimeIntervalData): string {
        const intervalDataKey: string = Object.keys(intervalData).find((key) => isDefined(intervalData[key]));
        const value: number = intervalData[intervalDataKey];
        const selectedOption: IAuditOnDemandOption = this.fiChecklist2ScheduleIntervalService.auditOnDemandOptions.find(
            (option) => {
                return option.intervalDataKey === intervalDataKey;
            },
        );
        return `${value} ${this.appTextService.get(value === 1 ? selectedOption.labelSingle : selectedOption.labelPlural).toLowerCase()}`;
    }

    private getMonthlyPeriodName(intervalData: IChecklistTimeIntervalData, monthName: string): string {
        switch (intervalData.DataType) {
            case FiChecklistTimeIntervalDataType.Day:
                return monthName + intervalData.Day;
            case FiChecklistTimeIntervalDataType.FirstWeekDay:
                return (
                    monthName +
                    this.appTextService.get('common.first').toLowerCase() +
                    ' ' +
                    this.fiLocaleService.getAllDayNames()[intervalData.DayOfWeek - 1].toLowerCase()
                );
            case FiChecklistTimeIntervalDataType.LastWeekDay:
                return (
                    monthName +
                    this.appTextService.get('common.last').toLowerCase() +
                    ' ' +
                    this.fiLocaleService.getAllDayNames()[intervalData.DayOfWeek - 1].toLowerCase()
                );
            case FiChecklistTimeIntervalDataType.LastDay:
                return monthName + this.fiLocaleService.getLastDayInMonth(intervalData.Month);
        }
    }
}
